import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Trash2, ShieldCheck, LogIn, LogOut, UserCheck, Sparkles, Settings2, BookOpen, Gauge, Mail } from "lucide-react";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import Brandmark from "@/components/Brandmark";
import SafetyLink from "@/components/SafetyLink";
import { http, getDeviceId } from "@/lib/api";
import { useApp } from "@/lib/store";
import { getReminder, enableReminder, disableReminder } from "@/lib/reminders";
import { Switch } from "@/components/ui/switch";

export default function Settings() {
  const { deviceId, refreshProgress, user, isAdmin, isPlus, logout, canInstall, promptInstall } = useApp();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [reminder, setReminder] = useState(getReminder());

  const toggleReminder = async () => {
    if (reminder.enabled) {
      setReminder(await disableReminder());
      toast("Daily reminder off");
    } else {
      const r = await enableReminder(reminder.time);
      setReminder(r);
      toast(r.granted
        ? "Daily reminder on"
        : "Reminder set — allow notifications for alerts when the app is closed");
    }
  };

  const changeReminderTime = async (time) => {
    setReminder((r) => ({ ...r, time }));
    if (reminder.enabled) setReminder(await enableReminder(time));
  };

  const manageBilling = () => nav("/manage-subscription");
  const download = (obj, name) => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = user ? await http.get("/auth/export") : await http.get(`/history/${deviceId}`);
      download(data, "signal-state-data.json");
      toast("Data exported");
    } catch (e) { toast("Export failed"); }
    setBusy(false);
  };

  const deleteAll = async () => {
    const msg = user
      ? "Permanently delete your account and all Signal State ID data?"
      : "Permanently delete all of your Signal State ID data on this device?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      if (user) {
        await http.delete("/auth/account");
        await logout();
      } else {
        const { data } = await http.get(`/history/${deviceId}`);
        await Promise.all(data.items.map((i) => http.delete(`/checkin/${i.id}`)));
      }
      localStorage.removeItem("ss_device_id");
      getDeviceId();
      refreshProgress();
      toast("All data deleted");
    } catch (e) { toast("Delete failed"); }
    setBusy(false);
  };

  return (
    <Shell>
      <div className="px-6 pt-14 pb-28">
        <Brandmark size={30} className="mb-3" />
        <h1 className="font-serif-display text-4xl">Settings</h1>

        <section className="mt-8" data-testid="settings-privacy-box">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Privacy</h2>
          <div className="mt-3 ss-card p-5">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} strokeWidth={1.6} className="text-[#C9A227]" />
              <span className="text-[15px] font-medium">Private &amp; nonclinical by design</span>
            </div>
            <p className="mt-2 text-[13.5px] leading-relaxed text-[#474747]">
              Your check-ins are yours. We never sell or share your data, and this is a self-reflection
              tool — not medical advice.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button data-testid="settings-privacy" onClick={() => nav("/privacy")}
                className="w-full border border-[#1A1A1A] rounded-full px-4 py-2.5 text-[13.5px] font-medium ss-tap ss-focus">
                Privacy Policy
              </button>
              <button data-testid="settings-terms" onClick={() => nav("/terms")}
                className="w-full border border-[#1A1A1A] rounded-full px-4 py-2.5 text-[13.5px] font-medium ss-tap ss-focus">
                Terms of Use
              </button>
            </div>
            <button data-testid="settings-faq" onClick={() => nav("/faq")}
              className="mt-3 w-full border border-[#1A1A1A] rounded-full px-4 py-2.5 text-[13.5px] font-medium ss-tap ss-focus">
              FAQ &amp; common questions
            </button>
          </div>
        </section>

        <section className="mt-6" data-testid="settings-history-box">
          <div className="ss-card p-5">
            <h3 className="text-[15px] font-medium">How your history works</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-[#474747]">
              Used <strong>anonymously</strong>, this browser remembers you and your history — until you clear
              your browser, switch browsers, or change phones. If that happens, the history won't follow you.
            </p>
            <p className="mt-2 text-[13.5px] leading-relaxed text-[#474747]">
              <strong>Signing in with your email</strong> makes your history durable and available on every
              device. When you sign in, the check-ins from this browser are merged into your account.
            </p>
            <div className="mt-3 rounded-xl bg-[#F5F3ED] p-3.5 text-[13px] leading-relaxed text-[#1A1A1A]">
              <p><strong>Anonymous:</strong> “this browser remembers me and my history until I clear it.”</p>
              <p className="mt-1"><strong>Signed in:</strong> “my history is mine everywhere, permanently.”</p>
            </div>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Account</h2>
          <div className="mt-3 ss-card p-5">
            {user ? (
              <>
                <div className="flex items-center gap-2">
                  <UserCheck size={18} strokeWidth={1.5} className="text-[#C9A227]" />
                  <span className="text-[15px] font-medium" data-testid="account-email">{user.email}</span>
                </div>
                <p className="mt-2 text-[13.5px] text-[#474747]">
                  Your history now follows you across devices.
                </p>
                {isAdmin && (
                  <button data-testid="settings-admin" onClick={() => nav("/admin")}
                    className="mt-4 w-full border border-[#C9A227] text-[#8a6d10] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus">
                    Open admin dashboard
                  </button>
                )}
                <button data-testid="settings-logout" onClick={() => logout()}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[#1A1A1A] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus">
                  <LogOut size={16} strokeWidth={1.5} /> Sign out
                </button>
              </>
            ) : (
              <>
                <p className="text-[14px] text-[#474747]">
                  Sign in with your email to keep your history across devices. No password needed.
                </p>
                <button data-testid="settings-signin" onClick={() => nav("/login")}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus">
                  <LogIn size={16} strokeWidth={1.5} /> Save my progress
                </button>
              </>
            )}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Plan</h2>
          <div className="mt-3 ss-card p-5" data-testid="settings-plan">
            {isPlus ? (
              <>
                <div className="flex items-center gap-2">
                  <Sparkles size={18} strokeWidth={1.6} className="text-[#C9A227]" />
                  <span className="text-[15px] font-medium" data-testid="plan-badge">Signal State ID Plus</span>
                </div>
                <p className="mt-2 text-[13.5px] text-[#474747]">
                  Pattern Review, PDF export and cross-device history are unlocked.
                </p>
                <button data-testid="settings-manage-billing" onClick={manageBilling}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-[#1A1A1A] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus">
                  <Settings2 size={16} strokeWidth={1.5} /> Manage subscription
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-medium" data-testid="plan-badge">Free</span>
                </div>
                <p className="mt-2 text-[13.5px] text-[#474747]">
                  Unlimited check-ins. Upgrade to Plus to unlock your Pattern Review and history across devices.
                </p>
                <button data-testid="settings-upgrade" onClick={() => nav("/plus")}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus hover:-translate-y-0.5">
                  <Sparkles size={16} strokeWidth={1.5} /> Upgrade to Plus
                </button>
              </>
            )}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Your data</h2>
          <div className="mt-3 ss-card divide-y divide-[#EBE9E4]">
            <button data-testid="export-data" onClick={exportData} disabled={busy}
              className="w-full flex items-center gap-3 p-4 text-left ss-focus">
              <Download size={18} strokeWidth={1.5} className="text-[#474747]" />
              <span className="text-[15px]">Export my data</span>
            </button>
            <button data-testid="delete-data" onClick={deleteAll} disabled={busy}
              className="w-full flex items-center gap-3 p-4 text-left ss-focus">
              <Trash2 size={18} strokeWidth={1.5} className="text-[#474747]" />
              <span className="text-[15px]">{user ? "Delete my account & data" : "Delete all my data"}</span>
            </button>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Reminders</h2>
          <div className="mt-3 ss-card p-5" data-testid="settings-reminders">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[15px] font-medium">Daily check-in reminder</div>
                <p className="mt-1 text-[13px] text-[#6B6B6B]">
                  A gentle, optional nudge. Off by default — no streaks, no pressure.
                </p>
              </div>
              <Switch data-testid="reminder-toggle" checked={reminder.enabled} onCheckedChange={toggleReminder} />
            </div>
            {reminder.enabled && (
              <div className="mt-4 flex items-center gap-3">
                <label className="text-[13.5px] text-[#474747]">Remind me at</label>
                <input
                  data-testid="reminder-time"
                  type="time"
                  value={reminder.time}
                  onChange={(e) => changeReminderTime(e.target.value)}
                  className="rounded-lg border border-[#EBE9E4] bg-white px-3 py-2 text-[14px] ss-focus"
                />
              </div>
            )}
            {reminder.enabled && !reminder.granted && (
              <p className="mt-3 text-[12.5px] text-[#8a6d10] leading-relaxed">
                Allow notifications to be reminded when the app is closed. Otherwise we'll nudge you
                gently the next time you open Signal State ID.
              </p>
            )}
          </div>
        </section>

        {canInstall && (
          <section className="mt-8">
            <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">App</h2>
            <div className="mt-3 ss-card p-5">
              <p className="text-[14px] text-[#474747]">
                Install Signal State ID for one-tap access — it works offline for the 60-second check-in.
              </p>
              <button data-testid="settings-install" onClick={promptInstall}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus">
                <Download size={16} strokeWidth={1.5} /> Add to home screen
              </button>
            </div>
          </section>
        )}

        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Privacy</h2>
          <div className="mt-3 ss-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={18} strokeWidth={1.5} className="text-[#C9A227]" />
              <span className="text-[15px] font-medium">Your data belongs to you</span>
            </div>
            <p className="text-[13.5px] leading-relaxed text-[#474747]">
              Signal State ID remembers only what helps you navigate, and you decide what remains true.
              Your internal-state information powers your history and pattern reviews. It is not sold to
              advertisers or shared with employers.
            </p>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Learn</h2>
          <div className="mt-3 ss-card">
            <button data-testid="settings-load-recovery" onClick={() => nav("/load-recovery")}
              className="w-full flex items-center gap-3 p-4 text-left ss-focus border-b border-[#EFEDE7]">
              <Gauge size={18} strokeWidth={1.5} className="text-[#474747]" />
              <span className="text-[15px]">Load &amp; Recovery</span>
            </button>
            <button data-testid="settings-operating-states" onClick={() => nav("/operating-states")}
              className="w-full flex items-center gap-3 p-4 text-left ss-focus">
              <BookOpen size={18} strokeWidth={1.5} className="text-[#474747]" />
              <span className="text-[15px]">Understanding operating states</span>
            </button>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Support</h2>
          <div className="mt-3 ss-card p-5">
            <p className="text-[14px] text-[#474747] leading-relaxed">
              Need help, have feedback, or want additional guidance? Reach out any time —
              we read every message.
            </p>
            <a
              href="mailto:support@signalstateid.com"
              data-testid="support-email-link"
              className="mt-4 flex items-center gap-3 rounded-xl border border-[#E7DFBE] bg-[#F7F2D7] px-4 py-3.5 ss-tap ss-focus transition-colors"
            >
              <Mail size={18} strokeWidth={1.75} color="#8a6d10" className="shrink-0" />
              <span className="flex flex-col">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">
                  Contact support
                </span>
                <span className="text-[15px] font-semibold text-[#1A1A1A]">
                  support@signalstateid.com
                </span>
              </span>
            </a>
            <div className="mt-4 border-t border-[#EBE9E4] pt-4">
              <SafetyLink />
            </div>
          </div>
        </section>

        <div className="mt-10 flex items-center justify-center">
          <p className="text-center text-[#6B6B6B] font-serif-display text-lg">
            Recognize your state. Choose your response.
          </p>
        </div>
      </div>
    </Shell>
  );
}
